// src/pages/ChatPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import { MessageCircle, Search, Send, Paperclip, Info } from "lucide-react";

import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import BackButton from "../components/BackButton";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import useTasklyWS from "../hooks/useTasklyWS";

/* ─────────────────────────  THEME (consistent with app)  ───────────────────────── */
const theme = {
  colors: {
    primary: "#3b82f6", // App blue - matches brand
    primaryDark: "#2563eb", // darker blue - matches brand
    primaryLight: "#eff6ff", // light blue focus ring / chips - matches brand
    primarySurface: "#f0f7ff",
    meBubble: "#eff6ff", // your bubble (light blue) - matches brand
    themBubble: "#ffffff", // other bubble (white)
    wallpaper: "#f8fafc", // chat background (app background)
    textHeading: "#0f172a", // app text color
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc", // app background
    surface: "#ffffff",
    border: "#e5e7eb",
    success: "#16a34a",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 10px 24px rgba(15, 23, 42, 0.09)",
  },
  radius: "14px",
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const shimmer = keyframes`
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
`;

const GlobalStyle = createGlobalStyle`
  body { background: ${theme.colors.background}; font-family: ${theme.font}; }
`;

/* ─────────────────────────  SKELETONS  ───────────────────────── */
const Skeleton = styled.div`
  width: ${(p) => p.w || "100%"};
  height: ${(p) => p.h || "14px"};
  border-radius: ${(p) => p.r || "12px"};
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s infinite linear;
`;
const SkCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius};
  padding: ${(p) => p.p || "12px"};
  box-shadow: ${theme.shadows.sm};
`;

/* ─────────────────────────  LAYOUT  ───────────────────────── */
const Page = styled.div`
  padding-top: 10px;
  animation: ${fadeIn} 0.2s ease;
`;

const Container = styled.div`
  width: 1120px;
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px;

  @media (max-width: 1200px) {
    width: 100%;
    padding: 16px;
  }

  @media (max-width: 1024px) {
    padding: ${(props) => (props.activeId ? "0" : "0")}; /* Full width for both sidebar and right pane */
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 1px; /* subtle separation */
  height: calc(100vh - 120px);
  border-radius: ${theme.radius};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  background: ${theme.colors.border}; /* for the gap */

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    height: calc(100vh - 100px);
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }
`;

/* ─────────────────────────  SIDEBAR (left pane)  ───────────────────────── */
const Sidebar = styled.aside`
  background: ${theme.colors.surface};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top-left-radius: ${theme.radius};
  border-bottom-left-radius: ${theme.radius};

  @media (max-width: 1024px) {
    display: ${(props) => (props.activeId ? "none" : "flex")};
    height: 100%;
    border-radius: 0;
  }
`;

const SidebarTop = styled.div`
  padding: 10px 12px;
  background: ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.border};

  h2 {
    color: ${theme.colors.textHeading};
    margin: 0 0 8px 0;
    font-size: 1.05rem;
    font-weight: 800;
    display: none;
  }

  @media (max-width: 1024px) {
    padding: 12px;
    h2 {
      display: block;
      font-size: 1.4rem;
      margin-bottom: 8px;
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  border-top: 1px solid ${theme.colors.border};
  padding-top: 10px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TabInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  font-size: 0.7rem;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin-top: 6px;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 8px 10px;
  background: ${({ active }) =>
    active ? "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)" : theme.colors.surface};
  color: ${({ active }) => (active ? "#fff" : theme.colors.textMuted)};
  border: 1px solid
    ${({ active }) => (active ? "#3b82f6" : theme.colors.border)};
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 1024px) {
    padding: 10px 12px;
    font-size: 0.85rem;
    min-height: 40px; /* Better touch target */
  }

  &:hover {
    background: ${({ active }) =>
      active ? "linear-gradient(135deg, #2563eb 0%, #5b5bf6 100%)" : theme.colors.background};
    border-color: ${({ active }) => 
      active ? "#2563eb" : "#3b82f6"};
    color: ${({ active }) => (active ? "#fff" : "#3b82f6")};
  }
`;

const SearchWrap = styled.div`
  position: relative;
  input {
    width: 100%;
    border: 1px solid ${theme.colors.border};
    border-radius: 12px;
    padding: 12px 16px 12px 44px;
    font-size: 0.95rem;
    color: ${theme.colors.textBody};
    outline: none;
    background: ${theme.colors.background};
    transition: all 0.2s ease;
    
    @media (max-width: 1024px) {
      padding: 14px 16px 14px 44px;
      font-size: 16px; /* Prevent zoom on iOS */
      border-radius: 10px;
    }
  }
  input:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
    background: ${theme.colors.surface};
  }
  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.6;
    color: ${theme.colors.textMuted};
    
    @media (max-width: 1024px) {
      left: 16px;
    }
  }
`;

const ConvList = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d7db;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const ConvItem = styled.button`
  width: calc(100% - 16px);
  background: ${(p) =>
    p.active ? theme.colors.primaryLight : theme.colors.surface};
  color: ${theme.colors.textBody};
  padding: 12px;
  margin: 2px 8px 6px 8px;
  display: grid;
  grid-template-columns: 48px 1fr auto;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
  border: 1px solid
    ${(p) => (p.active ? theme.colors.primary : theme.colors.border)};

  &:hover {
    background: ${(p) =>
      p.active ? theme.colors.primaryLight : theme.colors.background};
    border-color: ${theme.colors.primary};
  }

  @media (max-width: 1024px) {
    width: calc(100% - 12px);
    grid-template-columns: 44px 1fr auto;
    padding: 12px;
    margin: 2px 6px 4px 6px;
    min-height: 60px; /* Better touch target */
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 40px 1fr auto;
    padding: 14px 12px;
    min-height: 64px; /* Even better touch target on small screens */
  }
`;

const ConvTitle = styled.div`
  font-weight: 700;
  color: ${theme.colors.textHeading};
  font-size: 0.98rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ConvPreview = styled.div`
  margin-top: 2px;
  font-size: 0.82rem;
  color: ${theme.colors.textMuted};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MetaCol = styled.div`
  display: grid;
  justify-items: end;
  align-content: center;
  gap: 6px;
`;

const Badge = styled.span`
  background: ${theme.colors.primary};
  color: #fff;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 800;
  padding: 3px 8px;
`;

const RoleTag = styled.span`
  background: ${({ role }) => (role === "owner" ? "#eaf8ed" : "#e8f7f2")};
  color: ${({ role }) =>
    role === "owner" ? theme.colors.success : theme.colors.primary};
  border: 1px solid #d6e7da;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1;
`;

/* ─────────────────────────  AVATAR  ───────────────────────── */
const AvatarContainer = styled.div`
  width: ${(p) => p.size || 48}px;
  height: ${(p) => p.size || 48}px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  border: 1px solid #0000000a;
`;
const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const AvatarInitials = styled.div.attrs((props) => ({
  style: { background: props.bgColor || theme.colors.primary },
}))`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: ${(p) => (p.size > 40 ? "14px" : "12px")};
`;

/* ─────────────────────────  RIGHT PANE (messages)  ───────────────────────── */
const Pane = styled.main`
  background: ${theme.colors.surface};
  display: flex;
  flex-direction: column;
  min-height: 60vh;
  height: calc(100vh - 120px);
  overflow-x: hidden;
  border-top-right-radius: ${theme.radius};
  border-bottom-right-radius: ${theme.radius};

  @media (max-width: 1024px) {
    display: ${(props) => (props.activeId ? "flex" : "none")};
    height: 100vh;
    width: 100vw;
    max-width: 100vw;
    border-radius: 0;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 400010;
  }
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${theme.colors.surface};
  color: ${theme.colors.textHeading};
  border-top-right-radius: ${theme.radius};
  border-bottom: 1px solid ${theme.colors.border};

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 400011;
    flex-wrap: nowrap; /* Keep items inline */
    gap: 12px;
    border-radius: 0;
    padding: 8px 16px; /* Reduced padding */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    align-items: center; /* Center align items */
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 1024px) {
    gap: 8px; /* Tighter spacing on mobile */
  }
`;

const MobileBackButton = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    min-height: 36px;
    flex-shrink: 0; /* Prevent shrinking */
    
    /* Override BackButton's margin for inline alignment */
    button {
      margin-bottom: 0 !important;
      margin: 0;
      padding: 4px 8px; /* Reduced padding for header context */
    }
  }
`;

const HeadTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  line-height: 1.3;
  cursor: pointer;
  word-wrap: break-word;
  hyphens: auto;

  &:hover {
    color: ${theme.colors.primary};
  }
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

/* Messages area with wallpaper */
const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 18px 20px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  background-color: ${theme.colors.wallpaper};
  background-image: radial-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
    radial-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px);
  background-size: 24px 24px, 12px 12px;
  background-position: 0 0, 6px 6px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d7db;
    border-radius: 9999px;
  }

  @media (max-width: 1024px) {
    padding-top: 8px; /* Reduced since ChatInfoBar has better spacing now */
    padding-bottom: calc(110px + 20px); /* Account for enhanced fixed footer spacing */
    padding-left: 16px;
    padding-right: 16px;
  }

  * {
    min-width: 0;
    max-width: 100%;
    word-break: break-word;
  }
`;

const DayDivider = styled.div`
  position: relative;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: 12px;
  margin: 12px 0;
  padding: 0 10px;
  display: inline-block;
  align-self: center;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid ${theme.colors.border};
  border-radius: 999px;
`;

const Row = styled.div`
  display: flex;
  justify-content: ${(p) => (p.out ? "flex-end" : "flex-start")};
  padding: 2px 0;
  align-items: flex-end;
`;

const MsgWrap = styled.div`
  max-width: 640px;
  @media (max-width: 1024px) {
    max-width: min(640px, calc(100vw - 32px));
  }
  @media (max-width: 480px) {
    max-width: calc(100vw - 24px);
  }
`;

const Bubble = styled.div`
  position: relative;
  padding: 12px 16px;
  line-height: 1.5;
  font-size: 14px;
  color: ${(p) => (p.out ? theme.colors.textHeading : theme.colors.textBody)};
  background: ${(p) =>
    p.out ? theme.colors.meBubble : theme.colors.themBubble};
  border: 1px solid
    ${(p) => (p.out ? theme.colors.primary + "20" : theme.colors.border)};
  border-radius: ${(p) =>
    p.out ? "16px 16px 6px 16px" : "16px 16px 16px 6px"};
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: 1024px) {
    padding: 12px 14px;
    font-size: 15px; /* Slightly larger for mobile readability */
    line-height: 1.4;
    border-radius: ${(p) =>
      p.out ? "18px 18px 6px 18px" : "18px 18px 18px 6px"};
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 14px;
  }

  img,
  video {
    max-width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
    margin-top: 4px;
  }
  pre,
  code {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const Stamp = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  text-align: ${(p) => (p.out ? "right" : "left")};
`;

/* Composer (message input) */
const Composer = styled.form`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 16px 20px;
  background: ${theme.colors.surface};
  border-top: 1px solid ${theme.colors.border};
  border-bottom-right-radius: ${theme.radius};

  @media (max-width: 1024px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 400012;
    border-radius: 0;
    padding: 14px 16px;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
    background: ${theme.colors.surface};
    border-top: 2px solid ${theme.colors.border};
  }

  > * {
    min-width: 0;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  resize: none;
  max-height: 140px;
  min-height: 44px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.border};
  font-size: 14px;
  line-height: 1.4;
  background: ${theme.colors.background};
  color: ${theme.colors.textBody};
  transition: all 0.2s ease;
  font-family: ${theme.font};
  
  @media (max-width: 1024px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
    background: ${theme.colors.surface};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  cursor: pointer;
  color: ${theme.colors.textMuted};
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const Empty = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  color: ${theme.colors.textMuted};
  text-align: center;
  padding: 24px;
`;

/* Chips row above messages – now subtle */
const ChatInfoBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.8rem;
  width: 100%;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    position: relative;
    z-index: 1;
    margin-top: 60px; /* Reduced margin for smaller header */
    background: ${theme.colors.surface};
    border-bottom: 1px solid ${theme.colors.border};
  }
`;

const InfoCard = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const InfoIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;

  &.budget {
    background: #f1f5f9;
    color: ${theme.colors.textMuted};
  }
  &.offer {
    background: #f1f5f9;
    color: ${theme.colors.textMuted};
  }
  &.status {
    background: #f8fafc;
    color: ${theme.colors.textMuted};
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  line-height: 1.1;
`;
const InfoLabel = styled.span`
  color: ${theme.colors.textMuted};
  font-weight: 500;
  font-size: 0.7rem;
  text-transform: capitalize;
  letter-spacing: 0.1px;
`;
const InfoValue = styled.span`
  color: ${theme.colors.textBody};
  font-weight: 600;
  font-size: 0.8rem;
  &.amount {
    color: ${theme.colors.textBody};
    font-weight: 600;
  }
  &.status {
    text-transform: capitalize;
  }
`;

/* ─────────────────────────  HELPERS  ───────────────────────── */
function getLocalStore() {
  try {
    const raw = localStorage.getItem("store");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function getUserColor(userId) {
  const colors = [
    "#e11d48",
    "#db2777",
    "#c026d3",
    "#a21caf",
    "#9333ea",
    "#7c3aed",
    "#6366f1",
    "#3b82f6",
    "#0ea5e9",
    "#06b6d4",
    "#10b981",
    "#22c55e",
    "#84cc16",
    "#eab308",
    "#f59e0b",
    "#f97316",
  ];
  const index = userId ? Math.abs(userId) % colors.length : 0;
  return colors[index];
}
function getUserInitials(user) {
  if (!user) return "?";
  const name = user.name || "";
  if (name.trim()) {
    const parts = name.trim().split(" ");
    if (parts.length >= 2)
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  return "U";
}
const Avatar = ({ user, size = 48 }) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = user?.avatar && !imageError;
  if (hasValidImage) {
    return (
      <AvatarContainer size={size}>
        <AvatarImage
          src={user.avatar}
          alt={user.name || "User"}
          onError={() => setImageError(true)}
        />
      </AvatarContainer>
    );
  }
  const initials = getUserInitials(user);
  const bgColor = getUserColor(user?.id);
  return (
    <AvatarContainer size={size}>
      <AvatarInitials bgColor={bgColor} size={size}>
        {initials}
      </AvatarInitials>
    </AvatarContainer>
  );
};

function resolveMyId(state) {
  return (
    state?.user?.id ??
    state?.user?.user_id ??
    state?.auth?.user?.id ??
    state?.auth?.user_id ??
    getLocalStore()?.user?.id ??
    getLocalStore()?.user?.user_id ??
    null
  );
}
function normalizeMsg(m) {
  return {
    ...m,
    sender_id: m.sender_id ?? m.user_id ?? m.sender?.id ?? m.from_id ?? null,
  };
}

/* ─────────────────────────  PAGE  ───────────────────────── */
function ChatPage({ state }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [threadMeta, setThreadMeta] = useState(null);
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("my_tasks");

  const seenMsgIds = useRef(new Set());
  const myId = resolveMyId(state);
  const myIdStr = String(myId ?? "");
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    setActiveId(conversationId ? Number(conversationId) : null);
  }, [conversationId]);

  // Lambda warming for chat functionality
  useEffect(() => {
    const warmChatLambdas = async () => {
      const chatEndpoints = [
        { url: API_CONSTANT_MAP.get_conversation_messages, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.send_conversation_message, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.list_conversations, method: "POST", data: {} },
        { url: API_CONSTANT_MAP.mark_conversation_read, method: "POST", data: {} }
      ];

      // Fire warming requests in parallel, ignore errors
      const warmingPromises = chatEndpoints.map(async (endpoint) => {
        try {
          await request(endpoint.method, endpoint.url, endpoint.data);
        } catch (error) {
          // Silently ignore errors - we just want to warm the Lambdas
          console.debug(`Chat Lambda warming for ${endpoint.url}: OK`);
        }
      });

      // Don't await - let them run in background
      Promise.allSettled(warmingPromises);
    };

    // Warm chat Lambdas in background
    warmChatLambdas();
  }, []);

  const token =
    state?.auth?.token ??
    state?.user?.token ??
    getLocalStore()?.user?.token ??
    getLocalStore()?.auth?.token ??
    null;

  const bottom = useRef(null);

  /* WS push */
  useTasklyWS({
    token,
    enabled: location.pathname.startsWith("/chat"),
    onEvent: (evt) => {
      if (!evt?.type) return;

      if (evt.type === "message.created") {
        const cid = Number(evt.conversation_id ?? evt.message?.conversation_id);
        const message = normalizeMsg(evt.message ?? {});
        const mid = message?.id != null ? String(message.id) : null;

        if (mid && seenMsgIds.current.has(mid)) return;
        if (mid) seenMsgIds.current.add(mid);

        setConversations((prev) =>
          prev.map((c) =>
            Number(c.id) === cid
              ? {
                  ...c,
                  last_message_preview: message.body,
                  last_message_at: message.created_at,
                  last_sender_id: message.sender_id,
                  unread_count:
                    Number(activeId) === cid ? 0 : (c.unread_count || 0) + 1,
                }
              : c
          )
        );

        setMessages((prev) => {
          if (Number(activeId) !== cid) return prev;
          if (mid && prev.some((m) => String(m.id) === mid)) return prev;

          const idx = prev.findIndex(
            (m) =>
              String(m.id).startsWith("tmp_") &&
              String(m.sender_id) === myIdStr &&
              m.body === message.body
          );
          if (idx >= 0) {
            const copy = prev.slice();
            copy[idx] = message;
            return copy;
          }
          return [...prev, message];
        });
      }

      if (evt.type === "conversation.created") {
        setConversations((prev) =>
          prev.some((c) => Number(c.id) === Number(evt.conversation?.id))
            ? prev
            : [evt.conversation, ...prev]
        );
      }

      if (evt.type === "conversation.read") {
        const cid = Number(evt.conversation_id);
        setConversations((prev) =>
          prev.map((c) =>
            Number(c.id) === cid ? { ...c, unread_count: 0 } : c
          )
        );
      }
    },
  });

  /* Sidebar fetch */
  useEffect(() => {
    (async () => {
      try {
        const resp = await request("POST", API_CONSTANT_MAP.list_conversations);
        if (resp.data?.success) setConversations(resp.data.conversations || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Load thread */
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      setThreadMeta(null);
      seenMsgIds.current = new Set();
      return;
    }
    (async () => {
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.get_conversation_messages,
        { conversation_id: activeId }
      );
      if (resp.data?.success) {
        const list = (resp.data.messages || []).map(normalizeMsg);
        setMessages(list);
        setThreadMeta(resp.data.meta || null);
        seenMsgIds.current = new Set(list.map((m) => String(m.id)));

        const last = list[list.length - 1];
        if (last) {
          request("POST", API_CONSTANT_MAP.mark_conversation_read, {
            conversation_id: activeId,
            last_read_message_id: last.id,
          }).catch(() => {});
        }
      }
    })();
  }, [activeId]);

  /* autoscroll */
  useEffect(
    () => bottom.current?.scrollIntoView({ behavior: "smooth" }),
    [messages]
  );

  /* Send */
  const onSend = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text || !activeId || !myId) return;

    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      id: tempId,
      conversation_id: activeId,
      sender_id: myId,
      kind: "user",
      body: text,
      message_metadata: {},
      created_at: new Date().toISOString(),
    };

    setMessages((m) => [...m, optimistic]);
    setBody("");

    try {
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.send_conversation_message,
        { conversation_id: activeId, body: text }
      );

      if (resp?.data?.success && resp.data.message) {
        const real = normalizeMsg(resp.data.message);
        if (real?.id != null) seenMsgIds.current.add(String(real.id));

        setMessages((m) => {
          const hasWsReal =
            real?.id != null && m.some((x) => String(x.id) === String(real.id));
          if (hasWsReal) return m.filter((x) => x.id !== tempId);
          return m.map((x) => (x.id === tempId ? real : x));
        });

        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  last_message_preview: real.body,
                  last_message_at: real.created_at,
                  last_sender_id: real.sender_id,
                }
              : c
          )
        );
      } else {
        throw new Error("Bad response");
      }
    } catch {
      setMessages((m) => m.filter((msg) => msg.id !== tempId));
      alert("Failed to send message.");
    }
  };

  /* Filters */
  const filteredConversations = useMemo(() => {
    let filtered = conversations;
    if (activeTab === "my_tasks") {
      filtered = filtered.filter((c) => c.user_role === "owner");
    } else if (activeTab === "my_offers") {
      filtered = filtered.filter((c) => c.user_role === "provider");
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.task_title || "").toLowerCase().includes(q) ||
          (c.other_user?.name || "").toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [search, conversations, activeTab]);

  const renderMessages = () =>
    !resolveMyId(state) ? null : messages.length ? (
      messages.map((m, idx) => {
        const out = String(m.sender_id ?? "") === myIdStr;
        const day = parseDate(m.created_at).toDateString();
        const showDivider =
          idx === 0 ||
          day !== parseDate(messages[idx - 1].created_at).toDateString();
        return (
          <React.Fragment key={m.id}>
            {showDivider && <DayDivider>{day}</DayDivider>}
            <Row out={out}>
              <MsgWrap>
                <Bubble out={out}>{m.body}</Bubble>
                <Stamp out={out}>
                  {formatDistanceToNow(parseDate(m.created_at), {
                    addSuffix: true,
                  })}
                </Stamp>
              </MsgWrap>
            </Row>
          </React.Fragment>
        );
      })
    ) : (
      <Empty>
        <div>
          <MessageCircle size={48} />
          <div style={{ marginTop: 8 }}>No messages yet. Say hello 👋</div>
        </div>
      </Empty>
    );

  /* Skeleton UIs */
  const SidebarSkeleton = () => (
    <SkCard p="12px" style={{ borderRadius: 0, border: 0 }}>
      <Skeleton w="100%" h="38px" r="10px" />
      <div style={{ marginTop: 12, display: "grid", gap: 0 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              padding: 12,
              borderBottom: `1px solid ${theme.colors.border}`,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "42px 1fr auto",
                gap: 12,
                alignItems: "center",
              }}
            >
              <Skeleton w="42px" h="42px" r="999px" />
              <div style={{ minWidth: 0, overflow: "hidden" }}>
                <Skeleton w="100%" h="14px" />
                <div style={{ marginTop: 6 }}>
                  <Skeleton w="90%" h="12px" />
                </div>
              </div>
              <Skeleton w="44px" h="20px" r="999px" />
            </div>
          </div>
        ))}
      </div>
    </SkCard>
  );

  const PaneSkeleton = () => (
    <SkCard p="24px">
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <Skeleton w="200px" h="20px" style={{ margin: "0 auto 12px" }} />
        <Skeleton w="150px" h="14px" style={{ margin: "0 auto" }} />
      </div>
      <div style={{ display: "grid", gap: "16px" }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <Skeleton w="100%" h="16px" />
            <Skeleton w="80%" h="14px" style={{ marginTop: "8px" }} />
          </div>
        ))}
      </div>
    </SkCard>
  );

  return (
    <>
      <GlobalStyle />
      <NavBar user={state.user} />
      <OuterContainer>
        <Page>
          <Container activeId={activeId}>
            {loading ? (
              <Grid>
                <Sidebar>
                  <SidebarSkeleton />
                </Sidebar>
                <Pane>
                  <PaneSkeleton />
                </Pane>
              </Grid>
            ) : (
              <Grid>
                {/* Sidebar */}
                <Sidebar activeId={activeId}>
                  <SidebarTop>
                    <SearchWrap>
                      <Search size={18} />
                      <input
                        placeholder="Search conversations…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </SearchWrap>

                    <TabsContainer>
                      <TabRow>
                        <TabButton
                          active={activeTab === "my_tasks"}
                          onClick={() => setActiveTab("my_tasks")}
                        >
                          My Tasks
                        </TabButton>
                        <TabButton
                          active={activeTab === "my_offers"}
                          onClick={() => setActiveTab("my_offers")}
                        >
                          All Offers
                        </TabButton>
                      </TabRow>
                      {activeTab === "my_tasks" && (
                        <TabInfo>
                          <Info size={12} />
                          You can only see messages when you accept an offer and send a message - conversations will show up here
                        </TabInfo>
                      )}
                      {activeTab === "my_offers" && (
                        <TabInfo>
                          <Info size={12} />
                          You will only see messages here once your offers are accepted and the client sends a message
                        </TabInfo>
                      )}
                    </TabsContainer>
                  </SidebarTop>

                  <ConvList>
                    {filteredConversations.map((c) => {
                      let lastSenderId = c.last_sender_id;
                      if (
                        !lastSenderId &&
                        c.id === activeId &&
                        messages.length
                      ) {
                        lastSenderId = messages[messages.length - 1]?.sender_id;
                      }
                      const youSent = String(lastSenderId ?? "") === myIdStr;

                      return (
                        <ConvItem
                          key={c.id}
                          active={activeId === c.id}
                          onClick={() => navigate(`/chat/${c.id}`)}
                        >
                          <Avatar user={c.other_user} size={48} />

                          <div style={{ minWidth: 0 }}>
                            <ConvTitle
                              title={c.task_title || "Conversation"}
                              style={{ textTransform: "capitalize" }}
                            >
                              {c.task_title || "Conversation"}
                            </ConvTitle>
                            <ConvPreview
                              title={
                                (youSent
                                  ? "You: "
                                  : `${
                                      c.other_user?.name || "User"
                                    }: `) + (c.last_message_preview || "—")
                              }
                            >
                              {youSent
                                ? "You: "
                                : `${
                                    c.other_user?.name || "User"
                                  }: `}
                              {c.last_message_preview || "—"}
                            </ConvPreview>
                          </div>

                          <MetaCol>
                            {activeTab === "all" && c.user_role && (
                              <RoleTag role={c.user_role}>
                                {c.user_role === "owner"
                                  ? "My Task"
                                  : "My Offer"}
                              </RoleTag>
                            )}
                            <div
                              style={{
                                fontSize: 11,
                                color: theme.colors.textMuted,
                              }}
                            >
                              {c.last_message_at
                                ? formatDistanceToNow(
                                    new Date(c.last_message_at),
                                    { addSuffix: true }
                                  )
                                : ""}
                            </div>
                            {c.unread_count > 0 && (
                              <Badge>{c.unread_count}</Badge>
                            )}
                          </MetaCol>
                        </ConvItem>
                      );
                    })}
                  </ConvList>
                </Sidebar>

                {/* Message pane */}
                <Pane activeId={activeId}>
                  <PaneHeader>
                    <HeaderLeft>
                      <MobileBackButton>
                        <BackButton
                          size={15}
                          onClick={() => {
                            if (window.innerWidth <= 1024) {
                              navigate("/chat");
                            } else {
                              navigate(-1);
                            }
                          }}
                        />
                      </MobileBackButton>
                      <HeadTitle
                        title={threadMeta?.task_title || "Conversation"}
                        style={{ textTransform: "capitalize" }}
                        onClick={() => {
                          if (threadMeta?.task_id)
                            navigate(`/tasks/${threadMeta.task_id}`);
                        }}
                      >
                        {threadMeta?.task_title || "Conversation"}
                      </HeadTitle>
                    </HeaderLeft>
                  </PaneHeader>

                  {(() => {
                    const currentConv = conversations.find(
                      (c) => c.id === activeId
                    );
                    if (!currentConv) return null;

                    const hasTaskBudget = currentConv.task_budget != null;
                    const hasOfferInfo =
                      currentConv.offer_amount != null &&
                      currentConv.offer_status;
                    if (!hasTaskBudget && !hasOfferInfo) return null;

                    return (
                      <ChatInfoBar>
                        {hasTaskBudget && (
                          <InfoCard>
                            <InfoIcon className="budget">$</InfoIcon>
                            <InfoContent>
                              <InfoLabel>Task Budget</InfoLabel>
                              <InfoValue className="amount">
                                ${currentConv.task_budget}
                              </InfoValue>
                            </InfoContent>
                          </InfoCard>
                        )}
                        {hasOfferInfo && (
                          <>
                            <InfoCard>
                              <InfoIcon className="offer">$</InfoIcon>
                              <InfoContent>
                                <InfoLabel>
                                  {currentConv.user_role === "provider"
                                    ? "Your Offer"
                                    : "Their Offer"}
                                </InfoLabel>
                                <InfoValue className="amount">
                                  ${currentConv.offer_amount}
                                </InfoValue>
                              </InfoContent>
                            </InfoCard>
                            <InfoCard>
                              <InfoIcon className="status">
                                {currentConv.offer_status === "accepted"
                                  ? "✓"
                                  : currentConv.offer_status === "pending"
                                  ? "⏳"
                                  : currentConv.offer_status === "rejected"
                                  ? "✗"
                                  : "•"}
                              </InfoIcon>
                              <InfoContent>
                                <InfoLabel>Status</InfoLabel>
                                <InfoValue className="status">
                                  {currentConv.offer_status}
                                </InfoValue>
                              </InfoContent>
                            </InfoCard>
                          </>
                        )}
                      </ChatInfoBar>
                    );
                  })()}


                  {!activeId ? (
                    <Empty>
                      Select a conversation from the left to get started
                    </Empty>
                  ) : (
                    <Messages>
                      {renderMessages()}
                      <div ref={bottom} />
                    </Messages>
                  )}

                  <Composer onSubmit={onSend}>
                    <IconBtn type="button" title="Attach">
                      <Paperclip size={18} />
                    </IconBtn>
                    <Textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Type a message"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onSend(e);
                        }
                      }}
                    />
                    <IconBtn
                      as="button"
                      type="submit"
                      title="Send"
                      style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                        color: "#fff",
                        borderColor: "#3b82f6",
                      }}
                    >
                      <Send size={18} />
                    </IconBtn>
                  </Composer>
                </Pane>
              </Grid>
            )}
          </Container>
        </Page>
      </OuterContainer>
    </>
  );
}

export default connect((s) => ({ state: s }))(ChatPage);
